import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Alert,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import JudgeSignatureModal from "./JudgeSignatureModal";

// #732 — per-judge drawer: basic info + signature status, with a button that
// opens the HUGE signature capture modal. Same right-anchored Drawer pattern
// as RideHistoryDrawer (#507) for People/Horses.
export default function JudgeDrawer({ open, onClose, judge, onSignatureChanged, onUsefIdChanged }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [modalOpen, setModalOpen] = useState(false);

  // #3583 -- the judge's USEF id lives on the CENTRAL judge record (one per real
  // judge, shared by every org and show), so saving it here fixes every later upload.
  const storedUsefId = judge?.centralJudge?.usefId || "";
  const [usefIdInput, setUsefIdInput] = useState(storedUsefId);
  const [savingUsefId, setSavingUsefId] = useState(false);
  const [usefIdError, setUsefIdError] = useState(null);
  const [usefIdSaved, setUsefIdSaved] = useState(false);
  // ADR 0004: the 409 "that id belongs to someone with a different name" carries
  // the holder's name; staff answer it with a button, not by guessing.
  const [usefIdConflict, setUsefIdConflict] = useState(null);
  useEffect(() => {
    setUsefIdInput(storedUsefId);
    setUsefIdError(null);
    setUsefIdSaved(false);
    setUsefIdConflict(null);
  }, [judge?.id, storedUsefId]);

  // The central record's name when it is not this judge's own name (a link made
  // by name or by USEF id to a differently spelled person): staff can see it.
  const normName = (first, last) => `${first || ""} ${last || ""}`.trim().toLowerCase();
  const central = judge?.centralJudge;
  const linkedName =
    central && normName(central.firstName, central.lastName) !== normName(judge?.firstName, judge?.lastName)
      ? `${central.firstName || ""} ${central.lastName || ""}`.trim()
      : "";

  const saveUsefId = async (confirmLink = false) => {
    if (!judge) return;
    try {
      setSavingUsefId(true);
      setUsefIdError(null);
      setUsefIdSaved(false);
      setUsefIdConflict(null);
      unwrap(
        await scoreApi.judges.setJudgeUsefId(judge.id, {
          usefId: usefIdInput.trim(),
          ...(confirmLink ? { confirmLink: true } : {}),
        })
      );
      setUsefIdSaved(true);
      onUsefIdChanged?.();
    } catch (err) {
      const body = err?.response?.data;
      if (err?.response?.status === 409 && body?.code === "usef_id_held_by_other_judge") {
        setUsefIdConflict({ message: body?.data?.message, holderName: body?.data?.holderName });
      } else {
        setUsefIdError(body?.data?.message || err?.message || t("judges.drawer.usefIdSaveError"));
      }
    } finally {
      setSavingUsefId(false);
    }
  };

  const infoItems = judge
    ? [
        { label: t("judges.columns.fvdJudgeId"), value: judge.fvdJudgeId },
        { label: t("judges.columns.role"), value: judge.role },
        { label: t("judges.columns.country"), value: judge.country },
      ]
    : [];

  return (
    <>
      {/* AppLayout's AppBar (common-fe) is pinned at zIndex 1300 — same as MUI's
          default Modal z-index Drawer uses — so without bumping this it paints
          over the drawer's title/close button (same fix as RideHistoryDrawer). */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      >
        <Box sx={{ width: { xs: "100vw", sm: 480 }, p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h5" color="text.primary">
              {judge ? userDisplayName(judge) : ""}
            </Typography>
            <IconButton onClick={onClose} aria-label={t("judges.drawer.close")}>
              <CloseIcon />
            </IconButton>
          </Box>

          {infoItems.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {infoItems.map((item) => (
                <Typography key={item.label} variant="body2" color="text.secondary">
                  <strong>{item.label}:</strong> {item.value || "-"}
                </Typography>
              ))}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="h6" gutterBottom color="text.primary">
            {t("judges.drawer.usefIdTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {storedUsefId ? t("judges.drawer.usefIdHint") : t("judges.drawer.usefIdMissingHint")}
          </Typography>
          {linkedName && (
            <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
              {t("judges.drawer.usefIdLinkedTo", { name: linkedName })}
            </Typography>
          )}
          {usefIdConflict && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("judges.drawer.usefIdBelongsTo", { name: usefIdConflict.holderName })}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {usefIdConflict.message}
              </Typography>
              <Button variant="outlined" color="inherit" size="small" onClick={() => saveUsefId(true)} disabled={savingUsefId}>
                {t("judges.drawer.usefIdConfirmLink")}
              </Button>
            </Alert>
          )}
          {usefIdError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {usefIdError}
            </Alert>
          )}
          {usefIdSaved && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {t("judges.drawer.usefIdSaved")}
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 3 }}>
            <TextField
              size="small"
              label={t("judges.drawer.usefIdLabel")}
              value={usefIdInput}
              onChange={(e) => {
                setUsefIdInput(e.target.value);
                setUsefIdSaved(false);
              }}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
              disabled={!judge}
            />
            <Button
              variant="outlined"
              onClick={() => saveUsefId(false)}
              disabled={!judge || savingUsefId || usefIdInput.trim() === storedUsefId}
              sx={{
                color: colors.foxOrange,
                borderColor: colors.foxOrange,
                "&:hover": { borderColor: colors.foxOrange, bgcolor: "action.hover" },
              }}
            >
              {savingUsefId ? t("judges.drawer.usefIdSaving") : t("judges.drawer.usefIdSave")}
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="h6" gutterBottom color="text.primary">
            {t("judges.drawer.signatureTitle")}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Chip
              label={
                judge?.signaturePresent
                  ? t("judges.drawer.signatureOnFile")
                  : t("judges.drawer.signatureMissing")
              }
              color={judge?.signaturePresent ? "success" : "default"}
              variant={judge?.signaturePresent ? "filled" : "outlined"}
            />
          </Stack>

          {judge?.signaturePresent && judge?.signatureUrl && (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "#ffffff",
                p: 1,
                mb: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={judge.signatureUrl}
                alt={t("judges.drawer.signatureTitle")}
                sx={{ maxWidth: "100%", maxHeight: 120 }}
              />
            </Box>
          )}

          {/* #732 — brand's default "primary" outlined button is nearly
              invisible in dark mode (dark-grey on dark-grey); force
              foxOrange like other score FEs' primary actions. */}
          <Button
            variant="outlined"
            onClick={() => setModalOpen(true)}
            disabled={!judge}
            sx={{
              color: colors.foxOrange,
              borderColor: colors.foxOrange,
              "&:hover": { borderColor: colors.foxOrange, bgcolor: "action.hover" },
            }}
          >
            {judge?.signaturePresent
              ? t("judges.drawer.manageSignature")
              : t("judges.drawer.captureSignature")}
          </Button>
        </Box>
      </Drawer>

      <JudgeSignatureModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        judge={judge}
        onSaved={() => {
          setModalOpen(false);
          onSignatureChanged?.();
        }}
      />
    </>
  );
}
